import { useState } from 'react';
import { useWithdrawals } from '@/hooks/wallet';
import { Skeleton } from '@/components/ui/Skeleton';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks';
import { ChevronLeft, ChevronRight, ArrowDownLeft, Check, X, Clock } from 'lucide-react';
import { cn } from '@/utils/cn';

const statusIcons: Record<string, React.ElementType> = {
  APPROVED: Check,
  PENDING: Clock,
  FAILED: X,
  CANCELLED: X,
};

const statusLabels: Record<string, string> = {
  APPROVED: 'Concluído',
  PENDING: 'Pendente',
  FAILED: 'Falhou',
  CANCELLED: 'Cancelado',
};

const statusColors: Record<string, string> = {
  APPROVED: 'bg-green-100 text-green-700',
  PENDING: 'bg-yellow-100 text-yellow-700',
  FAILED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-700',
};

export function WithdrawHistoryPage() {
  const location = useLocation();
  const { user } = useAuth();
  const backPath = location.pathname.startsWith('/medico') || user?.role === 'DOCTOR' ? '/medico/carteira' : '/carteira';

  const [page, setPage] = useState(1);

  const { data, isLoading } = useWithdrawals(page);
  const withdrawals = data?.items ?? [];
  const totalPages = Math.ceil((data?.total ?? 0) / 20);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <Link
          to={backPath}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium mb-2 inline-block"
        >
          &larr; Voltar para Carteira
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Histórico de Saques</h1>
        <p className="text-sm text-gray-500 mt-1">Todos os saques realizados</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        {isLoading ? (
          <div className="p-6 space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        ) : withdrawals.length === 0 ? (
          <div className="p-6 text-center">
            <ArrowDownLeft size={32} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-400">Nenhum saque realizado</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {withdrawals.map((w) => {
              const StatusIcon = statusIcons[w.status] || Clock;
              return (
                <div
                  key={w.id}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'p-2 rounded-full',
                        statusColors[w.status] || 'bg-gray-100 text-gray-700'
                      )}
                    >
                      <StatusIcon size={16} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {formatCurrency(w.amount)}
                      </p>
                      <p className="text-xs text-gray-500">{formatDate(w.createdAt)}</p>
                    </div>
                  </div>
                  <span
                    className={cn(
                      'text-xs font-medium px-2.5 py-1 rounded-full',
                      statusColors[w.status] || 'bg-gray-100 text-gray-700'
                    )}
                  >
                    {statusLabels[w.status] || w.status}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Página {page} de {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              aria-label="Página anterior"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              aria-label="Próxima página"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
